import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Text, TextType } from '../../../../ui-components/components/text';
import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex/row';
import style from './customer-information.module.less';

import countryNameMessages from '../../../country-names.messages';
import DataDrivenLogoColumn from '../../../columns/data-driven-logo-column';
import { CustomerTags } from '../customer-tags/customer-tags';
import messages from '../../messages';

/**
 * Renders customer marketplace usernames
 * @param {object} props
 * @param {string} [props.className] - CSS class name for the wrapper div
 * @param {Array<{marketplaceId: string, username: string}>} props.customerMarketplaces
 * @returns {React.ReactElement[]}
 */
export const RenderCustomerMarketplaceUsernames = ({ className = '', customerMarketplaces }) => {
  return customerMarketplaces.map(({ marketplaceId, username }) => {
    return (
      <div key={marketplaceId} className={style[className]}>
        <DataDrivenLogoColumn
          dataDrivenLogoProps={{
            marketplaceId: Number.parseInt(marketplaceId, 10),
          }}
          message={username}
        />
      </div>
    );
  });
};

/**
 * Displays customer marketplace usernames with link to see all if more than 2
 * @param {object} props
 * @param {Array<{marketplaceId: string, username: string}>} props.customerMarketplaces
 * @param {Function} props.showMarketPlaceUsernames - Callback to show marketplace usernames modal
 * @param {string} [props.className] - CSS class name for the wrapper
 * @returns {React.ReactElement}
 */
const CustomerMarketplaces = ({ customerMarketplaces, showMarketPlaceUsernames, className = '' }) => {
  if (customerMarketplaces.length > 1) {
    return (
      <Button className={style['all-marketplaces-link']} onClick={() => showMarketPlaceUsernames(customerMarketplaces)} theme='link'>
        <FormattedMessage {...messages.seeAllMarketplaceUsernames} />
      </Button>
    );
  }
  return <RenderCustomerMarketplaceUsernames className={className} customerMarketplaces={customerMarketplaces} />;
};

const getLocalizedCountryName = (countryCode) => {
  const countryMessage = countryNameMessages[countryCode];
  if (!countryMessage) {
    return null;
  }
  return <FormattedMessage {...countryMessage} />;
};

/**
 * Displays customer information including address, tags, and marketplace usernames
 * @param {object} props
 * @param {object} props.customerDetails - Customer details object with address and marketplace data
 * @param {Array<object>} props.tags - Available tags for the customer
 * @param {Array<object>} props.selectedTags - Currently selected tags
 * @param {(tag: object) => void} props.onTagsToggled - Callback when a tag is toggled
 * @param {(marketplaces: Array<object>) => void} props.showMarketPlaceUsernames - Callback to show marketplace usernames modal
 * @param {() => void} props.onEditCustomer - Callback when edit button is clicked
 * @param {boolean} [props.isEditEnabled] - Whether the edit button should be enabled
 * @returns {React.ReactElement}
 */
export const CustomerInformation = ({
  customerDetails,
  tags,
  selectedTags,
  onTagsToggled,
  showMarketPlaceUsernames,
  onEditCustomer,
  isEditEnabled = false,
}) => {
  const { address = {}, marketplaces = [] } = customerDetails;
  return (
    <div className={style['modal-content-body-top-section']}>
      <div className={style['modal-content-top-left']}>
        <FlexRow className={style['row-spacing']}>
          <div className={style.field}>
            <FormattedMessage className={style.field} {...messages.nameLabel} />
          </div>
          <span className={style['customer-info-field-value']}>{address.name}</span>
          <Button disabled={!isEditEnabled} onClick={onEditCustomer} theme='link'>
            <Text textType={TextType.Paragraph} value={messages.editCustomer} />
          </Button>
        </FlexRow>

        <FlexRow className={style['row-spacing']}>
          <div className={style.field}>
            <FormattedMessage {...messages.companyLabel} />
          </div>
          <span className={style['customer-info-field-value']}>{address.company}</span>
        </FlexRow>

        <FlexRow className={style['row-spacing']}>
          <div className={style.field}>
            <FormattedMessage {...messages.addressLabel} />
          </div>
          <span className={style['customer-info-field-value']}>{address.line1}</span>
        </FlexRow>

        <FlexRow className={style['row-spacing']}>
          <div className={style.field}>
            <FormattedMessage {...messages.cityStateZipLabel} />
          </div>
          <span className={style['customer-info-field-value']}>
            {address.city}, {address.state} {address.postalCode}
          </span>
        </FlexRow>

        <FlexRow className={style['row-spacing']}>
          <div className={style.field}>
            <FormattedMessage {...messages.countryLabel} />
          </div>
          <span className={style['customer-info-field-value']}>{getLocalizedCountryName(address.countryCode)}</span>
        </FlexRow>

        <FlexRow className={style['row-spacing']}>
          <div className={style.field}>
            <FormattedMessage {...messages.emailLabel} />
          </div>
          <span className={style['customer-info-field-value']}>{customerDetails.email}</span>
        </FlexRow>
      </div>

      <div className={style['modal-content-top-right']}>
        <FlexRow className={style['row-spacing']}>
          <div className={style['top-right-field']}>
            <FormattedMessage {...messages.customerTagsLabel} />
          </div>
          <CustomerTags onTagToggled={onTagsToggled} selectedTags={selectedTags} tags={tags} />
        </FlexRow>

        <FlexRow className={style['row-spacing']}>
          <div className={style['top-right-field']}>
            <FormattedMessage {...messages.marketplaceUsernamesLabel} />
          </div>
          <div className={style['customer-info-field-value-marketplace']}>
            <CustomerMarketplaces
              className='customer-marketplace-username-wrapper'
              customerMarketplaces={marketplaces}
              showMarketPlaceUsernames={showMarketPlaceUsernames}
            />
          </div>
        </FlexRow>
      </div>
    </div>
  );
};
